import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Card,
  Col,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import type { ReporteListItem } from '../api';
import { Loader } from '../components';
import { cleanEmpresa, formatMXN, formatMXNShort, pctColor } from '../utils/format';
import { useEmpresa, EMPRESA_TODAS, type EmpresaOption } from '../empresa';

const { Title, Text } = Typography;

interface RfcGroup {
  rfc:             string;
  nombre_empresa?: string;
  runs:            ReporteListItem[];
  latest?:         ReporteListItem;
}

// Base = roster de EmpresaProvider, NO reportes: un cliente sin conciliaciones
// no tiene filas en `conciliacion` y no debe desaparecer. Se arranca del roster
// y se adjunta su último run; RFCs con reporte fuera del roster se agregan igual.
function buildGroups(reportes: ReporteListItem[], empresas: EmpresaOption[], nombresClientes: Record<string, string>): RfcGroup[] {
  const byRfc = new Map<string, RfcGroup>();
  for (const r of reportes) {
    const key = r.rfc.toUpperCase();
    let g = byRfc.get(key);
    if (!g) { g = { rfc: r.rfc, runs: [] }; byRfc.set(key, g); }
    if (!g.nombre_empresa && r.nombre_empresa) g.nombre_empresa = r.nombre_empresa;
    g.runs.push(r);
  }
  for (const g of byRfc.values()) g.latest = g.runs[0];

  const sobrantes: RfcGroup[] = [];
  for (const g of byRfc.values()) {
    if (!empresas.some(e => e.value.toUpperCase() === g.rfc.toUpperCase())) sobrantes.push(g);
  }

  const result: RfcGroup[] = [];
  for (const e of empresas) result.push(byRfc.get(e.value.toUpperCase()) ?? { rfc: e.value, runs: [] });
  result.push(...sobrantes);

  return result.map(g => ({
    ...g,
    nombre_empresa: nombresClientes[g.rfc.toUpperCase()] || g.nombre_empresa,
  }));
}

export function Dashboard() {
  const navigate = useNavigate();
  const { rfc, reportes, nombresClientes, empresas, loading, error } = useEmpresa();

  // El nombre de empresa de la conciliación (nombre_empresa) solo se llena
  // cuando el extracto se subió vía el flujo de carpeta IA/{nombre}/... — un
  // RFC conciliado por otra vía puede quedar sin nombre ahí aunque sí esté
  // registrado con nombre en /credenciales (roster de clientes con FIEL
  // cargada). nombresClientes (del EmpresaProvider) es esa fuente preferida
  // cuando existe — solo se llena para AdminGlobal.
  const groups = useMemo(() => {
    return buildGroups(reportes, empresas, nombresClientes);
  }, [reportes, empresas, nombresClientes]);

  // El selector global de empresa (en el Header) filtra el Dashboard igual
  // que antes filtraba su Select local.
  const filteredGroups = useMemo(
    () => (rfc === EMPRESA_TODAS ? groups : groups.filter(g => g.rfc === rfc)),
    [groups, rfc],
  );

  const stats = useMemo(() => {
    const latests = filteredGroups.map(g => g.latest).filter(Boolean);
    const total      = filteredGroups.length;
    const pct = (r: typeof latests[number]) => r?.resumen?.porcentaje_conciliado ?? 0;
    const alDia      = latests.filter(r => pct(r) >= 80).length;
    const atrasadas  = latests.filter(r => pct(r) <  60).length;
    const matched    = latests.reduce((s, r) => s + (r?.resumen?.matched ?? 0) + (r?.resumen?.matched_ia ?? 0), 0);
    const sinCfdi    = latests.reduce((s, r) => s + (r?.resumen?.sin_cfdi ?? 0), 0);
    const sinMov     = latests.reduce((s, r) => s + (r?.resumen?.sin_movimiento_banco ?? 0), 0);
    const totalBanco = latests.reduce((s, r) => s + (r?.resumen?.monto_total_banco ?? 0), 0);
    const diferencia = latests.reduce((s, r) => s + (r?.resumen?.diferencia ?? 0), 0);
    const pctGlobal  = total > 0
      ? Math.round(latests.reduce((s, r) => s + pct(r), 0) / total)
      : 0;
    return { total, alDia, atrasadas, matched, sinCfdi, sinMov, totalBanco, diferencia, pctGlobal };
  }, [filteredGroups]);

  if (loading) return <Loader tip="Cargando Dashboard..." />;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 20 }}>Dashboard</Title>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <StatCard label="EMPRESAS ACTIVAS"     value={stats.total} hint="Registradas" color="#2d6bc4" />
        <StatCard label="Empresas al Día"      value={stats.alDia} hint="≥ 80% Conciliado" color="#198754" />
        <StatCard label="CFDIs CONCILIADOS"    value={stats.matched.toLocaleString('es-MX')} hint="Total Conciliados" color="#6f42c1" />
        <StatCard label="SIN CFDI"             value={stats.sinCfdi} hint="Movimientos sin Factura" color="#ffc107" />
        <StatCard label="SIN MOVIMIENTO"       value={stats.sinMov} hint="Facturas sin Pago" color="#fd7e14" />
        <StatCard label="EMPRESAS ATRASADAS"   value={stats.atrasadas} hint="< 60% Conciliado" color="#dc3545" />
        <StatCard label="% CONCILIADO GLOBAL"  value={`${stats.pctGlobal}%`} hint="Promedio" color={pctColor(stats.pctGlobal)} />
        <StatCard label="TOTAL BANCO"          value={formatMXNShort(stats.totalBanco)} hint="Movimientos" color="#0d6efd" />
      </Row>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card title="Resumen Global">
            <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
              <Progress
                type="circle"
                percent={Math.min(stats.pctGlobal, 100)}
                size={120}
                strokeColor={pctColor(stats.pctGlobal)}
                format={() => `${stats.pctGlobal}%`}
              />
              <div style={{ flex: 1 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Statistic
                    title="Total en Banco"
                    value={formatMXN(stats.totalBanco)}
                    valueStyle={{ color: '#1a3a5c', fontSize: 18 }}
                  />
                  <Statistic
                    title="Diferencia Acumulada"
                    value={formatMXN(stats.diferencia)}
                    valueStyle={{
                      color: stats.diferencia > 0 ? '#dc3545' : '#198754',
                      fontSize: 18,
                    }}
                  />
                  <Space size="large">
                    <Text>Empresas activas: <strong>{stats.total}</strong></Text>
                    <Text>Al día: <strong style={{ color: '#198754' }}>{stats.alDia}</strong></Text>
                  </Space>
                </Space>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Alertas">
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {stats.sinCfdi > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  message={`${stats.sinCfdi} Movimientos sin CFDI`}
                  description="Movimientos Bancarios sin Factura Asociada"
                />
              )}
              {stats.sinMov > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  message={`${stats.sinMov} Facturas sin Movimiento`}
                  description="CFDIs sin Pago Bancario Registrado"
                />
              )}
              {stats.atrasadas > 0 && (
                <Alert
                  type="error"
                  showIcon
                  message={`${stats.atrasadas} Usuario${stats.atrasadas > 1 ? 's' : ''} Atrasado${stats.atrasadas > 1 ? 's' : ''}`}
                  description="Conciliación Menor al 60%"
                />
              )}
              {stats.sinCfdi === 0 && stats.sinMov === 0 && stats.atrasadas === 0 && (
                <Alert
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                  message="Todo al Día"
                  description="Sin Alertas Pendientes"
                />
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {filteredGroups.length > 0 && (
        <Card title="Estado por Empresa">
          <Row gutter={[12, 12]}>
            {filteredGroups.map(g => {
              const pct   = g.latest?.resumen?.porcentaje_conciliado ?? 0;
              const color = pctColor(pct);
              return (
                <Col xs={24} md={12} lg={8} key={g.rfc}>
                  <Card
                    hoverable
                    size="small"
                    onClick={() => navigate(`/reportes/${g.rfc}${g.latest?.id ? `?id=${g.latest.id}` : ''}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={6}>
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Text strong style={{ color: '#1a3a5c' }}>
                          {cleanEmpresa(g.nombre_empresa) || g.rfc}
                        </Text>
                        <Text strong style={{ color, fontSize: 16 }}>
                          {g.latest ? `${pct}%` : '—'}
                        </Text>
                      </Space>
                      <Space size={4}>
                        <Tag style={{ fontFamily: 'monospace' }}>{g.rfc}</Tag>
                        {g.latest?.resumen_por_moneda && Object.keys(g.latest.resumen_por_moneda).length > 1 && (
                          <Tag color="gold" style={{ fontSize: 10 }}>⚠ multi-moneda</Tag>
                        )}
                      </Space>
                      <Progress
                        percent={Math.min(pct, 100)}
                        size="small"
                        strokeColor={color}
                        showInfo={false}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {g.latest
                          ? `${g.runs.length} Ejecución${g.runs.length !== 1 ? 'es' : ''}${g.latest.fecha_ejecucion ? ` · Última ${g.latest.fecha_ejecucion.slice(0, 10)}` : ''}`
                          : 'Sin conciliaciones todavía'}
                      </Text>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}
    </div>
  );
}

interface StatCardProps {
  label:      string;
  value:      React.ReactNode;
  hint:       string;
  color:      string;
  fullValue?: string;
}

function StatCard({ label, value, hint, color, fullValue }: StatCardProps) {
  return (
    <Col xs={12} sm={8} md={6} lg={3}>
      <Card
        size="small"
        style={{ borderTop: `3px solid ${color}`, height: 132 }}
        title={fullValue}
        styles={{ body: { padding: '12px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' } }}
      >
        <Text
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Text>
        <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: '28px', height: 28 }}>
          {value}
        </div>
        <Text
          type="secondary"
          style={{
            fontSize: 11,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {hint}
        </Text>
      </Card>
    </Col>
  );
}
